/**
 * STEADY PROPS (the perf overhaul, Stage 1): a record of props that is the SAME object for as long as nothing it says
 * has changed, so a `memo` below it holds.
 *
 * Why it is needed: the shell re-renders on every presence heartbeat (any player's, every few seconds at a table),
 * and most of its handlers are made anew each time - they depend on the sync object, which presence replaces. The map
 * took each new function for a change and redrew itself in full: a probe counted 24 map renders in 45 idle seconds
 * with one player joined, half with no change at all and half with only new handlers.
 *
 * - A handler (`on…`, or a name the caller lists) becomes one stable function that calls the NEWEST handler (never
 *   a stale closure); a handler that is absent stays absent - several props mean something by being undefined. Any
 *   other function is compared as it is: it may be a query the map calls while drawing (`isLocked`).
 * - A plain record of verbs and data (scenes, the table's assets) gets the same treatment one level down, and is kept
 *   while its data is the same.
 * - A Set is kept while it holds the same ids (a player's own figures, rebuilt each render); a plain record or list of
 *   data, while it holds the same members (the table's defaults, rebuilt each render).
 * - Everything else is compared as it is: data that changed is a new record, and refs pass through untouched.
 *
 * Pure apart from two refs: the previous record, and the newest props (set in an insertion effect, so before any
 * layout effect below can call a handler).
 */
import { useInsertionEffect, useRef } from 'react';

const isPlain = (v) => v !== null && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;
const hasVerbs = (o) => Object.values(o).some((v) => typeof v === 'function');
const sameSet = (a, b) => b instanceof Set && a.size === b.size && [...a].every((x) => b.has(x));
const at = (record, path) => path.reduce((o, k) => o?.[k], record);

function sameFields(a, b) {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => Object.is(a[k], b[k]));
}

/** A plain record or an array with the same members as `before`, one level deep. */
function sameData(value, before) {
  if (Array.isArray(value)) return Array.isArray(before) && sameFields(value, before);
  return isPlain(value) && isPlain(before) && sameFields(value, before);
}

/** A ref: `{ current }` and nothing else. Never a record of verbs, whatever it holds: a copy would take its writes. */
const isRef = (value) => Object.keys(value).length === 1 && 'current' in value;

/** A record of verbs and data, one level down from the props (never deeper: depth 1 stops there). */
const isVerbRecord = (value, depth) => depth === 0 && isPlain(value) && !isRef(value) && hasVerbs(value);

/**
 * Is the function at `path` an ACTION, safe to hand on as a relay? An event handler (`on…`, React's own convention),
 * a verb of a record (scenes' `cut`), or a name the caller lists (an async read called from an effect). Anything
 * else may be a QUERY the receiver calls while it draws (`isLocked`): its new identity is the only sign its answers
 * changed, so it is compared as it is - relayed, a player handed Deployment never saw the mode (2026-10-29).
 */
const EVENT = /^on[A-Z]/;
const isAction = (path, relay) => path.length > 1 || EVENT.test(path[0]) || relay.has(path[0]);

/**
 * One field, steadied: `before` is the previous record's steady value; `depth` 1 inside a record of verbs. Data that
 * is rebuilt equal is replaced by the previous one, never copied (so a ref is always the ref itself).
 */
const steadyFunction = (value, path, relayFor) => (relayFor.isAction(path) ? relayFor(path) : value);

function steadyValue(value, before, path, relayFor, depth) {
  if (typeof value === 'function') return steadyFunction(value, path, relayFor);
  if (value instanceof Set) return sameSet(value, before) ? before : value;
  if (isVerbRecord(value, depth)) return steadyRecord(value, isPlain(before) ? before : null, path, relayFor, 1);
  return sameData(value, before) ? before : value;
}

function steadyRecord(next, prev, path, relayFor, depth) {
  const out = {};
  for (const [k, v] of Object.entries(next)) out[k] = steadyValue(v, prev?.[k], [...path, k], relayFor, depth);
  return prev && sameFields(out, prev) ? prev : out;
}

/**
 * `props`, steadied. See the header. `relay`: top-level names, beyond the `on…` handlers, that are actions too.
 */
export default function useSteadyProps(props, { relay = [] } = {}) {
  const latest = useRef(props);
  useInsertionEffect(() => {
    latest.current = props;
  });
  const relays = useRef(new Map());
  const relayFor = (path) => {
    const key = path.join('.');
    if (!relays.current.has(key)) relays.current.set(key, (...args) => at(latest.current, path)?.(...args));
    return relays.current.get(key);
  };
  const named = new Set(relay);
  relayFor.isAction = (path) => isAction(path, named);
  const prev = useRef(null);
  const steady = steadyRecord(props, prev.current, [], relayFor, 0);
  prev.current = steady;
  return steady;
}
